import { Link } from "@/lib/react-router-dom-shim";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Star, ShoppingCart } from "lucide-react";
import { Product } from "@/hooks/useProducts";
import { useCart } from "@/hooks/useCart";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { ProgressiveImage } from "@/components/ProgressiveImage";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import { usePrice } from "@/hooks/usePrice";

const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <Star key={`full-${i}`} className="h-4 w-4 fill-yellow-400 text-yellow-400" aria-hidden="true" />
    );
  }

  if (hasHalfStar) {
    stars.push(
      <div key="half" className="relative h-4 w-4" aria-hidden="true">
        <Star className="h-4 w-4 text-yellow-400" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        </div>
      </div>
    );
  }

  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <Star key={`empty-${i}`} className="h-4 w-4 fill-muted text-muted" aria-hidden="true" />
    );
  }

  return <>{stars}</>;
};

interface ProductCardProps {
  product: Product;
  detailsUrl?: string;
  onAddToCart?: () => Promise<void>;
  isRecommended?: boolean;
}

export default function ProductCard({ product, detailsUrl, onAddToCart, isRecommended }: ProductCardProps) {
  const { addItem } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { format: formatPrice } = usePrice();
  const images = Array.isArray(product.images) ? product.images : [];
  const mainImage = images[0] || "/placeholder.svg";
  const productUrl = detailsUrl || `/producto/${product.id}`;

  const handleAddClick = async () => {
    if (onAddToCart) {
      setIsAddingToCart(true);
      try {
        await onAddToCart();
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        setIsAddingToCart(false);
      }
    } else {
      addItem({ productId: product.id });
    }
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'hotel': return t('nav.hotels');
      case 'car': return t('nav.cars');
      case 'activity': return t('nav.activities');
      case 'transfer': return 'Transfer';
      default: return type;
    }
  };

  return (
    <Card className={`overflow-hidden hover:shadow-elegant-hover transition-all duration-300 flex flex-col h-full group ${
      isRecommended 
        ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800 ring-1 ring-amber-200/50 dark:ring-amber-700/30' 
        : ''
    }`}>
      <Link to={productUrl} aria-label={`${t('common.seeMore')} ${product.name_es || product.name}`}>
        <div className="relative h-40 sm:h-48 overflow-hidden bg-muted">
          <ProgressiveImage
            src={mainImage}
            alt={product.name_es || product.name}
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            rootMargin="100px"
          />
          {/* Recommended Badge */}
          {isRecommended && (
            <div className="absolute top-2 left-2 z-10">
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg px-2 py-1 text-xs font-semibold gap-1">
                <Star className="h-3 w-3 fill-white" />
                {t('hotelDetail.recommendedHotel')}
              </Badge>
            </div>
          )}
          <Badge className="absolute top-2 right-2 bg-background/90 text-foreground text-xs z-10">
            {getProductTypeLabel(product.product_type)}
          </Badge>
        </div>
      </Link>

      <CardContent className="p-3 sm:p-4 flex-1 flex flex-col">
        <Link to={productUrl}>
          <h3 className="font-semibold text-base sm:text-lg mb-1.5 line-clamp-2 hover:text-primary transition-colors leading-tight">
            {product.name_es || product.name}
          </h3>
        </Link>
        
        {product.location && (
          <div className="flex items-start gap-1 text-xs sm:text-sm text-muted-foreground mb-2">
            <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span className="line-clamp-1">{product.location}</span>
          </div>
        )}

        {product.metadata?.starRating && (
          <div className="flex items-center gap-1 mb-2" aria-label={`${t('hotels.rating')} ${product.metadata.starRating}`}>
            <StarRating rating={product.metadata.starRating} />
          </div>
        )}

        {product.metadata?.roomDescription && (
          <p className="text-xs sm:text-sm font-medium text-foreground mb-1 line-clamp-1">
            {product.metadata.roomDescription}
          </p>
        )}

        {product.metadata?.boardDescription && (
          <p className="text-xs text-muted-foreground mb-1.5">
            {translateBoardType(product.metadata.boardDescription, locale)}
          </p>
        )}

        {product.metadata?.cancellationInfo && (
          <div className={`text-xs px-2 py-0.5 rounded mb-2 inline-block w-fit ${
            product.metadata.cancellationInfo.type === 'non-refundable'
              ? 'bg-destructive/10 text-destructive' 
              : 'bg-green-500/10 text-green-700 dark:text-green-400'
          }`}>
            {product.metadata.cancellationInfo.text}
          </div>
        )}

        <div className="flex flex-col gap-0.5 mt-auto pt-2">
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl sm:text-2xl font-bold text-primary">
              {formatPrice(
                product.metadata?.pricePerNight
                  ? Number(product.metadata.pricePerNight)
                  : Number(product.price_eur)
              )}
            </span>
            <span className="text-xs sm:text-sm text-muted-foreground">{t('common.perNight')}</span>
          </div>
          {product.metadata?.totalNights && product.metadata.totalNights > 1 && (
            <span className="text-xs text-muted-foreground">
              {t('common.total')}: {formatPrice(Number(product.price_eur))} ({product.metadata.totalNights} {product.metadata.totalNights === 1 ? t('common.night') : t('common.nights')})
            </span>
          )}
          {product.metadata?.propertyTaxesEur && product.metadata.propertyTaxesEur > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              + {formatPrice(Number(product.metadata.propertyTaxesEur))} {t('hotels.localTaxes')}
            </span>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-3 sm:p-4 pt-0 flex gap-2">
        <Button 
          variant="outline"
          size="icon"
          className="h-9 w-9 sm:h-10 sm:w-10 shrink-0"
          onClick={handleAddClick}
          disabled={isAddingToCart}
          aria-label={`${t('common.addToCart')} ${product.name_es || product.name}`}
          title={t('common.addToCart')}
        >
          {isAddingToCart ? (
            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
          ) : (
            <div className="flex items-center gap-0.5">
              <span className="text-base sm:text-lg font-bold leading-none">+</span>
              <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
            </div>
          )}
        </Button>
        <Button asChild className="flex-1 h-9 sm:h-10 text-sm">
          <Link to={productUrl}>{t('common.seeMore')}</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
